// ProductDetail.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';

import IntercomArticleModal from '../container/IntercomArticleModal.js';
import ModalView from '../container/ModalView.js';
import ProductDetailList from '../container/ProductDetailList.js';
import DecryptionWrapper from '../container/DecryptionWrapper.js';

import Loading from '../component/Loading.jsx';
import StatefulImage from '../component/StatefulImage.jsx';

import {
  headerHeight,
  color,
  iPhoneXSeriesFooterPaddingInPWA,
} from '../style/variables.js';
import media from '../style/media.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import TextEllipsis from '../style/TextEllipsis.js';
import ResetHeadingStyle from '../style/ResetHeadingStyle.js';

import getResourceUrl from '../resource/getResourceUrl.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';
import { ButtonId } from '../resource/mixpanel.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import {
  OrderStatus,
  InternalCurrency,
  ProductType,
} from '../resource/shopConstants.js';
import getDecimalFactorByCurrency from '../resource/getDecimalFactorByCurrency.js';
import { getIsInBrowserMainThread } from '../resource/getJsEnvironment.js';
import formatNumber from '../resource/formatNumber.js';
import numberWithCommas from '../resource/numberWithCommas.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import formatCurrencyText from '../resource/formatCurrencyText.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';

import UnlockIconSource from '../../img/ic_unlock_white.svg';
import DiamondGreyLineIconSource from '../../img/ic_diamond_greyline.svg';
import DiamondIconSource from '../../img/ic-diamond-xs.svg';
import CollectionIconSource from '../../img/ic_collection.svg';
import InfoIconSource from '../../img/ic_info_20.svg';

const isClient = getIsInBrowserMainThread();
const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();

class ProductDetail extends React.PureComponent {
  formatPrice = ({ price, currency } = {}) => {
    const decimalFactor = getDecimalFactorByCurrency({ currency });
    const isDiamondCurrency = InternalCurrency.DIAMONDS === currency;
    if (isClient && !isDiamondCurrency && currency) {
      return (
        formatCurrencyText({
          value: price / decimalFactor,
          currency,
          option: {
            currencyDisplay: 'code',
          },
        }) || price / decimalFactor
      );
    } else {
      return price / decimalFactor;
    }
  };
  getTrackingPayload = ({ extraProps: originExtraProps } = {}) => {
    const { productId, productType, feedName, secondaryBadge, messageIds } =
      this.props;
    const extraProps = {
      ...originExtraProps,
      'product.id': productId,
      'product.type': productType,
    };
    if (secondaryBadge.length) {
      const [, discountValue] = secondaryBadge;
      extraProps['product.discount'] = discountValue;
    }
    if (feedName && messageIds.length) {
      extraProps['ui_context.feed_alias'] = feedName;
      extraProps[`ui_context.feed_alias.${feedName}.message_ids`] =
        messageIds.join(',');
    }

    return extraProps;
  };
  fetchOrders = () => {
    const { productId, productType, fetchOrders } = this.props;
    if (!productId || !productType) {
      return;
    }
    return fetchOrders({
      productIds: [productId],
      orderStatus: OrderStatus.BOUGHT,
      productType,
    });
  };
  renderMetadata = ({ icon, value, alt }) => {
    if (value == null) {
      return null;
    }
    return (
      <Metadata>
        <img src={icon} alt={alt} />
        {formatNumber(value)}
      </Metadata>
    );
  };
  renderCta = ({ isOnDesktop } = {}) => {
    const {
      meId,
      productId,
      shopId,
      selectedSkuId,
      price,
      currency,
      productType,
      productNbf,
      productExp,
      timestampOffsetSeconds,
      isProductDetailFetching,
      isBoughtOrderFetching,
      isOrderSubmitting,
      isCompleteOrderSubmitting,
      boughtProductIds,
      buyProduct,
      login,
      t,
    } = this.props;
    if (!selectedSkuId) {
      return null;
    }
    const now = getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    });
    const isValidPurchaseTime =
      (productNbf != null ? now >= productNbf : true) &&
      (productExp != null ? now <= productExp : true);
    const isDiamondCurrency = InternalCurrency.DIAMONDS === currency;
    const isBought = boughtProductIds.includes(productId);
    const isLoading =
      isProductDetailFetching ||
      isBoughtOrderFetching ||
      isOrderSubmitting ||
      isCompleteOrderSubmitting;
    const isButtonDisabled =
      (ProductType.MESSAGEPACKS === productType ? isBought : false) ||
      isLoading ||
      !isValidPurchaseTime;
    return (
      <CtaButton
        as={isOnDesktop ? DesktopCtaButton : MobileCtaButton}
        disabled={isButtonDisabled}
        data-element_id={ButtonId.All.ButtonPurchase}
        data-tracking_payload={this.getTrackingPayload()}
        onClick={() => {
          if (!meId) {
            return login();
          }
          // User can't choose currency and payment method currently, so current flow is:
          // User click purchase button -> Create order with default currency -> Complete order with first payment method
          return buyProduct({
            shopId,
            skuId: selectedSkuId,
            currency,
            mustSelectPaymentSource: true,
          });
        }}
      >
        {isLoading ? (
          <Loading />
        ) : isBought || !isValidPurchaseTime ? (
          <CtaText isDisabled>
            {t(isBought ? 'purchased' : 'unavailable_for_sale', {
              ns: TranslationNamespace.SHOP,
            })}
          </CtaText>
        ) : (
          <>
            <CtaText>
              {t('purchase_message_pack', {
                ns: TranslationNamespace.SHOP,
              })}
            </CtaText>
            <CtaPrice>
              {isDiamondCurrency && <img src={DiamondIconSource} aria-hidden />}
              {this.formatPrice({ price, currency })}
            </CtaPrice>
          </>
        )}
      </CtaButton>
    );
  };
  renderMessagePackInformation = () => {
    const {
      formattedArticleLink,
      productId,
      publicProductUrlPath,
      currency,
      totalCount,
      discountedFrom,
      soldCount,
      secondaryBadge,
      shopColorBadges,
      t,
    } = this.props;
    const poster = getResourceUrl({
      path: publicProductUrlPath,
      endpoint: `/${productId}.jpg`,
      resourceType: PUBLIC,
    }).href;
    const [discountKey, discountValue] = secondaryBadge;
    const productName = t(
      getTranslationKey({ key: `shop_product_name_${productId}` }),
      {
        ns: TranslationNamespace.PRODUCT,
      }
    );
    const isDiamondCurrency = InternalCurrency.DIAMONDS === currency;
    return (
      <MessagePackInformation>
        <MessagePackInformationColumn gridArea={'left'}>
          <MessagePackImageWrapper>
            <StatefulImage>
              <div data-key="loading" />
              <div data-key="error" />
              <DecryptionWrapper resourceUrl={poster} data-key="target">
                <MessagePackImage src={poster} alt={productName} />
              </DecryptionWrapper>
            </StatefulImage>
            <MessageCount>
              <img src={CollectionIconSource} aria-hidden />
              {totalCount != null && numberWithCommas({ number: totalCount })}
            </MessageCount>
          </MessagePackImageWrapper>
        </MessagePackInformationColumn>
        <MessagePackInformationColumn gridArea={'right'}>
          <MessagePackTitle>{productName}</MessagePackTitle>
          <MessagePackMetadata>
            {this.renderMetadata({
              icon: UnlockIconSource,
              value: soldCount,
              alt: 'amount of unlocks',
            })}
          </MessagePackMetadata>
          <MessagePackPrice>
            {discountedFrom != null && (
              <OriginalPrice>
                {isDiamondCurrency && (
                  <img src={DiamondGreyLineIconSource} aria-hidden />
                )}
                {this.formatPrice({ price: discountedFrom, currency })}
                <OriginPriceStrikeThrough />
              </OriginalPrice>
            )}
            {secondaryBadge.length ? (
              <Discount
                badgeColor={
                  shopColorBadges[secondaryBadge[0]] ||
                  shopColorBadges['default']
                }
              >
                {t(
                  getTranslationKey({
                    key: `shop_product_badge_${discountKey}`,
                  }),
                  {
                    discount: discountValue,

                    ns: TranslationNamespace.PRODUCT,
                  }
                )}
              </Discount>
            ) : null}
          </MessagePackPrice>
          {this.renderCta({ isOnDesktop: true })}
          <InfoButtonLink
            data-element_id={ButtonId.All.ButtonInformation}
            data-tracking_payload={{
              'article.url': formattedArticleLink,
            }}
            href={formattedArticleLink}
            target="_blank"
            rel="noreferrer noopener"
          >
            <img
              src={InfoIconSource}
              alt={t('swagr_about_more_information', {
                ns: TranslationNamespace.GENERAL,
              })}
              width="28"
              height="28"
            />
          </InfoButtonLink>
        </MessagePackInformationColumn>
        {this.renderCta({ isOnMobile: true })}
      </MessagePackInformation>
    );
  };
  componentDidMount() {
    this.fetchOrders();
  }
  componentDidUpdate(prevProps) {
    const { meId, productId, productType } = this.props;
    const isMeIdChanged = meId !== prevProps.meId;
    const isProductIdChanged = productId !== prevProps.productId;
    const isProductTypeChanged = productType !== prevProps.productType;
    if (isMeIdChanged || isProductIdChanged || isProductTypeChanged) {
      this.fetchOrders();
    }
  }
  render() {
    const { productId, feedName } = this.props;
    return (
      <StyledMessagePackDetail>
        <Body>
          <InformationWrapper>
            {this.renderMessagePackInformation()}
          </InformationWrapper>
          <ProductListWrapper isIphoneXSeriesPWA={isIphoneXSeriesPWA}>
            <ProductDetailList productId={productId} feedName={feedName} />
          </ProductListWrapper>
        </Body>
        <ModalView id="IntercomArticleModal">
          <IntercomArticleModal />
        </ModalView>
      </StyledMessagePackDetail>
    );
  }
}
ProductDetail.propTypes = {
  formattedArticleLink: PropTypes.string,
  meId: PropTypes.string,
  feedName: PropTypes.string,
  productId: PropTypes.string,
  shopId: PropTypes.string,
  selectedSkuId: PropTypes.string,
  productType: PropTypes.string,
  publicProductUrlPath: PropTypes.string,
  currency: PropTypes.string,
  isProductDetailFetching: PropTypes.bool,
  isBoughtOrderFetching: PropTypes.bool,
  isOrderSubmitting: PropTypes.bool,
  isCompleteOrderSubmitting: PropTypes.bool,
  messageIds: PropTypes.array,
  boughtProductIds: PropTypes.array,
  shopColorBadges: PropTypes.object,
  totalCount: PropTypes.number,
  productNbf: PropTypes.number,
  productExp: PropTypes.number,
  timestampOffsetSeconds: PropTypes.number,
  discountedFrom: PropTypes.number,
  price: PropTypes.number,
  soldCount: PropTypes.number,
  secondaryBadge: PropTypes.array,
  fetchOrders: PropTypes.func,
  buyProduct: PropTypes.func,
  login: PropTypes.func,
  t: PropTypes.func.isRequired,
};

ProductDetail.defaultProps = {
  articleConfigKey: null,
  formattedArticleLink: null,
  meId: null,
  feedName: null,
  productId: null,
  shopId: null,
  selectedSkuId: null,
  productType: null,
  publicProductUrlPath: '',
  currency: '',
  isProductDetailFetching: false,
  isBoughtOrderFetching: false,
  isOrderSubmitting: false,
  isCompleteOrderSubmitting: false,
  messageIds: [],
  boughtProductIds: [],
  shopColorBadges: {},
  totalCount: null,
  productNbf: null,
  productExp: null,
  timestampOffsetSeconds: 0,
  discountedFrom: null,
  price: null,
  soldCount: null,
  secondaryBadge: [],
  fetchOrders: () => null,
  buyProduct: () => null,
  toggleModal: () => null,
  login: () => null,
};

const StyledMessagePackDetail = styled.div`
  position: relative;
  padding-top: ${headerHeight}px;
`;

const Body = styled.main`
  min-height: calc(100vh - ${headerHeight}px);
`;

const InformationWrapper = styled.div`
  position: sticky;
  top: ${headerHeight}px;
  left: 0px;
  z-index: 2;
  background-color: ${color.neutral[0]};
`;

const MaxWidhtStyle = css`
  margin: 0px auto;
  max-width: 960px;
  ${media.tablet`
    margin: 0px auto;
    max-width: 686px;
  `};
  ${media.mobile`
    margin: 0px;
    max-width: none;
  `};
`;

const BoundaryStyle = css`
  border-right: 1px solid rgba(71, 71, 71, 0.4);
  border-left: 1px solid rgba(71, 71, 71, 0.4);
  ${media.tablet`
    border-right: none;
    border-left: none;
  `};
`;

const MessagePackInformation = styled.div`
  ${MaxWidhtStyle};
  ${BoundaryStyle};
  border-bottom: 1px solid rgba(71, 71, 71, 0.4);
  padding: 20px;
  display: grid;
  grid-template-areas: 'left right';
  grid-template-columns: 160px auto;
  grid-column-gap: 12px;
  ${media.tablet`
    padding: 20px 60px;
    max-width: none;
  `};
  ${media.mobile`
    padding: 20px 16px;
    grid-template-areas:
      'left right'
      'cta cta';
    grid-template-columns: 100px auto;
    background-color: ${color.neutral[10]};
  `};
`;

const MessagePackInformationColumn = styled.div.attrs(({ gridArea }) => ({
  style: {
    gridArea,
  },
}))`
  position: relative;
`;

const InfoButtonLink = styled.a`
  ${ResetButtonStyle};
  position: absolute;
  top: 0px;
  right: 0px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  > img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
  ${media.mobile`
    display: none;
  `};
`;

const MessagePackImageWrapper = styled.div`
  position: relative;
  border-radius: 4px;
  grid-area: poster;
  overflow: hidden;
  width: 160px;
  height: 160px;
  background-color: ${color.neutral[30]};
  ${media.mobile`
    width: 100px;
    height: 100px;
  `};
`;

const MessagePackImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MessageCount = styled.div`
  position: absolute;
  bottom: 0px;
  left: 0px;
  padding: 0px 28px;
  width: 100%;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(25, 25, 25, 0.8);
  font-size: 16px;
  color: ${color.neutral[100]};
  > img {
    margin-right: 4px;
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
  ${media.mobile`
    height: 20px;
    font-size: 12px;
    > img {
      width: 12px;
      height: 12px;
    }
  `};
`;

const MessagePackTitle = styled.h1`
  ${ResetHeadingStyle};
  margin-bottom: 4px;
  padding-right: 48px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  grid-area: title;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.5;
  overflow: hidden;
  word-break: break-word;
  ${media.mobile`
    margin-bottom: 8px;
    padding-right: 0px;
    font-size: 16px;
  `};
`;

const MessagePackMetadata = styled.div`
  margin-bottom: 4px;
  grid-area: metadata;
  display: flex;
  align-items: center;
  ${media.mobile`
    margin-bottom: 8px;
  `};
`;

const Metadata = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  color: ${color.neutral[30]};
  > img {
    margin-right: 4px;
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
  ${media.mobile`
    font-size: 12px;
    > img {
      width: 12px;
      height: 12px;
    }
  `};
`;

const MessagePackPrice = styled.div`
  margin-bottom: 8px;
  grid-area: price;
  display: flex;
  align-items: center;
  ${media.mobile`
    margin-bottom: 0px;
  `};
`;

const OriginalPrice = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  color: ${color.neutral[30]};
  > img {
    margin-right: 4px;
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
  ${media.tablet`
    font-size: 16px;
    > img {
      margin-right: 2px;
      width: 16px;
      height: 16px;
    }
  `};
  ${media.mobile`
    font-size: 12px;
    > img {
      width: 12px;
      height: 12px;
    }
  `};
`;

const OriginPriceStrikeThrough = styled.div`
  position: absolute;
  left: 0px;
  right: 0px;
  height: 1px;
  background-color: ${color.red};
`;

const Discount = styled.div`
  margin-left: 12px;
  border-radius: 2px;
  padding: 2px 4px;
  background-color: ${color.red};
  font-size: 14px;
  color: ${color.neutral[100]};
  :first-child {
    margin-left: 0px;
  }
  ${media.tablet`
    margin-left: 8px;
    padding: 2px;
  `};
  ${media.mobile`
    margin-left: 8px;
    padding: 2px;
    font-size: 12px;
  `};
`;

const CtaButton = styled.button`
  ${ResetButtonStyle};
  border-radius: 4px;
  border: 1px solid ${color.tealBlue};
  padding: 0px 12px;
  width: 100%;
  height: 40px;
  grid-area: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  line-height: 1.5;
  :disabled {
    border-color: rgba(71, 71, 71, 0.8);
    background-color: rgba(35, 35, 35, 0.8);
  }
  ${media.tablet`
    height: 32px;
  `};
  ${media.mobile`
    margin-top: 12px;
    height: 32px;
    display: flex;
    grid-area: cta;
  `};
`;

const DesktopCtaButton = styled(CtaButton)`
  position: absolute;
  bottom: 0px;
  right: 0px;
  display: flex;
  ${media.mobile`
    display: none;
  `};
`;

const MobileCtaButton = styled(CtaButton)`
  display: none;
  ${media.mobile`
    display: flex;
  `};
`;

const CtaText = styled.span.attrs(({ isDisabled }) => ({
  style: {
    color: isDisabled ? 'rgba(255, 255, 255, 0.2)' : color.tealBlue,
  },
}))`
  ${TextEllipsis};
  font-size: 20px;
  font-weight: 600;
  ${media.tablet`
    font-size: 16px;
  `};
  ${media.mobile`
    font-size: 16px;
  `};
`;

const CtaPrice = styled.div`
  margin-left: 8px;
  display: flex;
  align-items: center;
  font-size: 22px;
  font-weight: 600;
  color: ${color.tealBlue};
  > img {
    margin-right: 4px;
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
  ${media.tablet`
    margin-left: 4px;
    font-size: 16px;
    > img {
      margin-right: 2px;
      width: 16px;
      height: 16px;
    }
  `};
  ${media.mobile`
    margin-left: 4px;
    font-size: 16px;
    > img {
      margin-right: 2px;
      width: 16px;
      height: 16px;
    }
  `};
`;

const ProductListWrapper = styled.div`
  ${MaxWidhtStyle};
  ${BoundaryStyle};
  padding-bottom: ${({ isIphoneXSeriesPWA }) =>
    isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0}px;
`;

export default withTranslation()(ProductDetail);
